import React, { useState, useEffect, ChangeEvent, useCallback, useContext } from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '@marty-js/design/src/utils/mq';

import { HeadingTwo } from '@marty-js/design/src/atoms/h2';

import { Cta } from '@marty-js/design/src/molecules/cta';
import { useTranslation } from '@marty-js/design/src/utils/translation';
import Heart from '@marty-js/design/src/icons/heart';
import Delete from '@marty-js/design/src/icons/material-symbols/delete';
import Comment from '@marty-js/design/src/icons/comment';
import Close from '@marty-js/design/src/icons/close';
import { Comments as CommentsIcon } from '@marty-js/design/src/icons/comments';

import { ActionBar } from '@marty-js/design/src/molecules/action-bar';
import { addComment, deleteComment, likeComment } from '@marty-js/api-sdk/services/sso';
import { getApiClient } from '@marty-js/api-sdk';
import { TopicInfo, TopicPostAll } from '@marty-js/api-sdk/types';
import { displayDateDiff } from './date/utils';
import { AuthContext } from '../utils/AuthContext';

const CommentWidget = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacer-fluid);
  margin: var(--spacer-fluid) 0;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: var(--spacer-fluid) 0 0;
    `,
  )}
`;

const WidgetElements = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  word-break: break-word;

  img {
    height: auto;
    margin: var(--spacer-s) 0;
    max-width: 100%;
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      padding: 0 0 0 75px;
    `,
  )}
`;

const Posts = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} />;

const Post = styled.div`
  border-bottom: 1px solid #dddddd;
  line-height: 29px;
  margin: 0 0 30px;
  padding: 0 0 30px;

  &:last-of-type {
    margin: initial;
  }

  && li {
    list-style: none;
  }

  && blockquote {
    font-size: 16px;
    font-style: italic;
    margin-bottom: 5px;
  }

  && aside.onebox {
    border: 5px solid #e6edf1;
    margin: 1em 0;
    padding: 1em;
    font-size: 1rem;
    background: var(--theme-palette-white);
  }

  && aside.onebox header {
    align-items: center;
    display: flex;
    margin-bottom: 1em;
  }
  && aside.onebox .onebox-body {
    clear: both;
  }

  && aside.onebox .onebox-body .aspect-image {
    max-height: 170px;
    max-width: 20%;
    float: left;
    margin-right: 1em;
    height: auto;
  }

  && aside {
    font-size: 16px;
    font-style: italic;
    border-left: 4px solid var(--theme-palette-red);
    margin-left: 15px;
    padding-left: 10px;
  }

  && .lightbox-wrapper .lightbox {
    position: relative;
    display: inline-block;
    overflow: hidden;
    &:hover .meta {
      opacity: 0.9;
      transition: opacity 0.5s;
    }
  }

  && .d-lazyload-hidden {
    box-sizing: border-box;
  }

  && .lightbox-wrapper {
    display: inline-block;
    img {
      object-fit: cover;
      object-position: top;
    }
    &,
    * {
      outline: 0;
    }
  }

  && .meta {
    display: none;
  }
`;

const PostAuthor = styled.div`
  align-items: center;
  display: flex;
  gap: 15px;
  font-weight: bold;
  margin: 0 0 15px;
`;

const PostAuthorInformations = styled.div`
  display: flex;
  flex-direction: column;
  font-family: var(--theme-typography-primary-font);
  font-size: 22px;
  gap: 7px;
  line-height: 20px;
`;

const ReplyTo = styled.div`
  font-size: 13px;
  line-height: 10px;
`;

const CtaReadMore = styled.div`
  cursor: pointer;
`;

const TimeAgo = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
`;

const AvatarImg = styled.img`
  border-radius: 50%;
`;

const TextArea = styled.textarea`
  background: none;
  border-color: var(--theme-palette-dark-grey-blue);
  border-radius: 2px;
  outline: none;
  height: 150px;
  line-height: 29px;
  max-width: 100%;
  padding: 8px 15px;
  width: 100%;
  color: var(--text-color-alt);

  &::placeholder {
    color: #99a4ba;
  }
`;

const GoToForum = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} />;

const CtaOpenForum = styled.a`
  align-items: center;
  color: var(--text-color-alt);
  display: flex;
  font-family: var(--theme-typography-primary-font);
  font-size: 18px;
  font-weight: 700;
  justify-content: center;
  gap: 10px;
  width: 100%;
  cursor: pointer;
`;

const PostReplyWrapper = styled.div`
  position: relative;
`;

interface FetchDataVariables {
  id: string;
  userToken: string;
  offset: number;
}

type FetchDataFunction = (variables: { variables: FetchDataVariables }) => void;

export type Props = {
  topic: TopicInfo;
  fetchData: FetchDataFunction;
  itemId: string;
  loading: boolean;
  loadMoreComments?: () => void;
  offset: number;
};

export const Comments = ({ topic, fetchData, itemId, loading, loadMoreComments, offset }: Props) => {
  const [showAll, setShowAll] = useState(true);
  const [userToken, setUserToken] = useState<string>(null);
  const [replyingToPostId, setReplyingToPostId] = useState(null);
  const [message, setMessage] = useState<string>('');
  const [commentRaw, setCommentRaw] = useState<string>('');
  const [commentRawReplyTo, setCommentRawReplyTo] = useState<string>('');
  const [triedToLike, setTriedToLike] = useState<number>(null);
  const { ssoUrl, username } = useContext(AuthContext);

  const apiClient = getApiClient();
  const [topicDiscourse, setTopic] = useState(topic);

  const checkPayload = useCallback(async () => {
    const width = 600;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    if (ssoUrl) {
      localStorage.setItem('urlBeforeLogin', document.documentURI);
      window.open(ssoUrl, 'clubic_login_window', `width=${width},height=${height},left=${left},top=${top}`);
    }
  }, [ssoUrl]);

  useEffect(() => {
    if (topic && !loading) {
      setTopic(topic);
    }
  }, [topic, loading, setTopic]);

  const handleTextAreaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setCommentRaw(event.target.value);
  };

  const handleTextAreaChangeReplyTo = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setCommentRawReplyTo(event.target.value);
  };

  const t = useTranslation();

  useEffect(() => {
    setUserToken(localStorage.getItem('userToken') ?? null);
    if (itemId) {
      const topicInProgress = JSON.parse(localStorage.getItem(`commentInProgress_${itemId}`));
      if (topicInProgress?.commentRaw) {
        setCommentRaw(topicInProgress.commentRaw);
        setShowAll(true);
      }
    }
    if (topicDiscourse?.postsCount === 1) {
      setShowAll(true);
    }
  }, [itemId, topicDiscourse]);

  const toggleShowAll = () => {
    setShowAll(!showAll);
  };

  const removePostReplyWrapper = useCallback(() => {
    setReplyingToPostId(null);
  }, [setReplyingToPostId]);

  const addCommentPost = useCallback(
    async (topicId: number = null, postId: number = null, type: string = 'replyTopic') => {
      const comment = { topicId, postId, type, commentRaw };
      localStorage.setItem(`commentInProgress_${itemId}`, JSON.stringify(comment));
      if (userToken) {
        if (type === 'replyPost' && topicId && postId) {
          await addComment(apiClient, topicId, postId, commentRawReplyTo);
          setCommentRawReplyTo('');
          setReplyingToPostId(null);
        } else if (type === 'newPost' && itemId) {
          await addComment(apiClient, null, null, commentRaw, itemId);
          setCommentRaw('');
        } else if (topicId) {
          await addComment(apiClient, topicId, null, commentRaw);
          setCommentRaw('');
        }
        fetchData({ variables: { id: itemId, userToken, offset } });
        localStorage.removeItem(`commentInProgress_${itemId}`);
      } else {
        setMessage(t('sdk.comments.reply_to_needs_login'));
      }
    },
    [apiClient, commentRaw, commentRawReplyTo, fetchData, itemId, offset, userToken, t],
  );

  const LikeComment = useCallback(
    async (postId: number, canAct: boolean = false, acted: boolean = false) => {
      setTriedToLike(postId);
      if (userToken && canAct && !acted) {
        await likeComment(apiClient, postId, 'like');

        fetchData({ variables: { id: itemId, userToken, offset } });
      } else if (userToken && acted) {
        await likeComment(apiClient, postId, 'unlike');
        fetchData({ variables: { id: itemId, userToken, offset } });
      } else if (!userToken) {
        checkPayload();
      }
    },
    [apiClient, checkPayload, fetchData, itemId, offset, userToken],
  );

  const replyTo = (postId: number) => {
    setReplyingToPostId(postId);
  };

  const deletePost = useCallback(
    async (postId: number) => {
      if (userToken) {
        await deleteComment(apiClient, postId);
        setTopic((prevTopic) => {
          if (!prevTopic?.postStream?.posts) {
            return prevTopic;
          }

          return {
            ...prevTopic,
            postStream: {
              ...prevTopic.postStream,
              posts: prevTopic.postStream.posts.filter((post) => post.id !== postId),
            },
          };
        });
      }
    },
    [apiClient, userToken],
  );

  const locale = t('locale');

  return (
    <CommentWidget id="comments">
      <HeadingTwo className="mod-without-m" as="div">
        {t('sdk.comments.title', { count: `(${topicDiscourse?.postsCount ?? 0})` ?? '' })}
      </HeadingTwo>
      {topicDiscourse?.postsCount > 0 && (
        <Posts>
          {topicDiscourse?.postStream?.posts
            .filter((post) => post?.cooked && post.cooked.trim() !== '')
            .slice(0, showAll ? topicDiscourse?.postStream?.posts.length : 1)
            .map((post) => {
              const dateTimeAgo = displayDateDiff(post.createdAt, locale);
              const likes = post.actionsSummary.find(({ actionId }) => actionId === 2);

              let postReplyTo: TopicPostAll = null;
              if (post.replyToPostNumber) {
                postReplyTo = topicDiscourse?.postStream?.allPosts.find(
                  ({ postNumber }) => postNumber === post.replyToPostNumber,
                );
              }

              const avatar = post.avatarTemplate.replace('{size}', '60');

              const pattern = /\/u\/[^\s]+/g;
              const commentCooked = post.cooked.replace(pattern, (match) => `https://forum.clubic.com${match}`);

              return (
                <Post key={post.id}>
                  <PostAuthor>
                    <AvatarImg src={`https://forum.clubic.com/${avatar}`} height={60} alt="username" />
                    <PostAuthorInformations>
                      {post.username}
                      <TimeAgo>{dateTimeAgo}</TimeAgo>
                    </PostAuthorInformations>
                  </PostAuthor>

                  <WidgetElements>
                    {post.replyToPostNumber && (
                      <ReplyTo>{t('sdk.comments.responseTo', { username: postReplyTo?.username })}</ReplyTo>
                    )}
                    <div dangerouslySetInnerHTML={{ __html: commentCooked }} />
                    <ActionBar
                      actions={[
                        {
                          icon: <Heart width={22} height={22} color="var(--title-foreground)" active={likes?.acted} />,
                          label: `${likes?.count ?? 0} ${t('sdk.comments.labels.like')}`,
                          onClick: () => LikeComment(post.id, likes?.canAct, likes?.acted),
                          active: likes?.acted,
                          hoverable: true,
                        },
                        {
                          icon: <Comment width={22} height={22} color="var(--title-foreground)" />,
                          label: t('sdk.comments.labels.reply'),
                          onClick: () => replyTo(post.id),
                        },
                        (post.yours || post.username === username) && {
                          icon: <Delete width={22} height={22} color="var(--title-foreground)" />,
                          label: t('sdk.comments.labels.delete'),
                          onClick: () => deletePost(post.id),
                        },
                        replyingToPostId === post.id && {
                          icon: <Close width={22} height={22} color="var(--title-foreground" />,
                          label: '',
                          onClick: () => removePostReplyWrapper(),
                          floatRight: true,
                          altColors: true,
                        },
                      ].filter(Boolean)}
                    />
                    {!userToken && triedToLike === post.id && <p>{t('sdk.comments.like_needs_login')}</p>}
                    {replyingToPostId && replyingToPostId === post.id && (
                      <PostReplyWrapper>
                        <TextArea
                          onChange={handleTextAreaChangeReplyTo}
                          onClick={() => {
                            if (!userToken) {
                              checkPayload();
                            }
                          }}
                          value={commentRawReplyTo}
                          placeholder={
                            userToken ? t('sdk.comments.disclaimer') : t('sdk.comments.anonymous_disclaimer')
                          }
                        />
                        <Cta
                          className="mod-blue"
                          onClick={() => addCommentPost(topicDiscourse?.id, post.postNumber, 'replyPost')}
                        >
                          {t('sdk.comments.replyTo', { username: post.username })}
                        </Cta>
                      </PostReplyWrapper>
                    )}
                  </WidgetElements>
                </Post>
              );
            })}
        </Posts>
      )}
      {topicDiscourse?.postStream?.streams.length > offset + 19 && (
        <CtaOpenForum onClick={loadMoreComments}>{t('sdk.comments.seeMore')}</CtaOpenForum>
      )}

      {!showAll && topicDiscourse?.postStream?.posts.length >= 1 && (
        <CtaReadMore>
          <Cta className="mod-blue" onClick={toggleShowAll}>
            {t('sdk.comments.seeAllCommentsAndAnswer')}
          </Cta>
        </CtaReadMore>
      )}

      <>
        <TextArea
          onClick={() => {
            if (!userToken) {
              checkPayload();
            }
          }}
          onChange={handleTextAreaChange}
          value={commentRaw}
          placeholder={userToken ? t('sdk.comments.disclaimer') : t('sdk.comments.anonymous_disclaimer')}
        />
        <Cta
          className="mod-blue"
          onClick={() => addCommentPost(topicDiscourse?.id || null, null, topicDiscourse ? undefined : 'newPost')}
        >
          {t('sdk.comments.post')}
        </Cta>
        {message && <p>{message}</p>}
      </>

      {topicDiscourse && (
        <GoToForum>
          <CtaOpenForum target="_blank" href={`https://forum.clubic.com/t/${topicDiscourse?.id}`}>
            <CommentsIcon width={26} height={26} color="var(--theme-palette-dark-grey-blue)" />
            {t('sdk.comments.seeForum')}
          </CtaOpenForum>
        </GoToForum>
      )}
    </CommentWidget>
  );
};
